/**
 * Atribución CTA Mejores Amigos → checkout MP (sessionStorage, TTL 30 min).
 * Correlaciona clicks hero con landing / conversión en PostHog.
 */

export type MejoresAmigosCtaSource =
  | "hero_mejores_amigos_base_template"
  | "hero_mejores_amigos_base_template_black"
  | "direct"
  | "unknown";

export type MejoresAmigosCtaAttribution = {
  source: MejoresAmigosCtaSource;
  source_template?: string;
  source_path: string;
  source_url: string;
  href: string;
  label?: string;
  element_id?: string;
  surface?: string;
  country_code?: string;
  province?: string;
  cta_tier?: string;
  cta_geo_source?: string;
  cta_resolved?: string;
  mp_plan_id?: string;
  clicked_at: string;
  ph_session_id?: string;
};

const STORAGE_KEY = "mejores_amigos_cta_attribution_v1";
const TTL_MS = 30 * 60 * 1000;

export function storeMejoresAmigosCtaAttribution(
  attrs: Omit<MejoresAmigosCtaAttribution, "clicked_at"> & { clicked_at?: string },
): void {
  if (typeof window === "undefined") return;
  try {
    const payload: MejoresAmigosCtaAttribution = {
      ...attrs,
      clicked_at: attrs.clicked_at ?? new Date().toISOString(),
    };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    /* private mode / quota */
  }
}

export function readMejoresAmigosCtaAttribution(): MejoresAmigosCtaAttribution | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as MejoresAmigosCtaAttribution;
    if (!parsed?.clicked_at || !parsed.source) {
      sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
    const age = Date.now() - new Date(parsed.clicked_at).getTime();
    if (!Number.isFinite(age) || age > TTL_MS) {
      sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function clearMejoresAmigosCtaAttribution(): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export function getMejoresAmigosAttributionPostHogProps(): Record<
  string,
  string | number | boolean
> {
  const attr = readMejoresAmigosCtaAttribution();
  if (!attr) {
    return { mejores_amigos_from_cta: false };
  }
  const secondsSinceClick = Math.max(
    0,
    Math.round((Date.now() - new Date(attr.clicked_at).getTime()) / 1000),
  );
  return {
    mejores_amigos_from_cta: true,
    mejores_amigos_cta_source: attr.source,
    mejores_amigos_cta_source_template: attr.source_template ?? "",
    mejores_amigos_cta_source_path: attr.source_path,
    mejores_amigos_cta_source_url: attr.source_url,
    mejores_amigos_cta_href: attr.href,
    mejores_amigos_cta_element_id: attr.element_id ?? "",
    mejores_amigos_cta_surface: attr.surface ?? "",
    mejores_amigos_cta_country: attr.country_code ?? "",
    mejores_amigos_cta_province: attr.province ?? "",
    mejores_amigos_cta_tier: attr.cta_tier ?? "",
    mejores_amigos_cta_geo_source: attr.cta_geo_source ?? "",
    mejores_amigos_cta_resolved: attr.cta_resolved ?? "",
    mejores_amigos_mp_plan_id: attr.mp_plan_id ?? "",
    mejores_amigos_cta_seconds_since_click: secondsSinceClick,
  };
}